<template>
  <div>
    <div class="export-container">
      <div class="title" v-if="sites && sites.length > 0">
        {{ sites[activeTab].siteName }}
      </div>
      <b-button
        type="is-primary"
        icon-left="microsoft-excel"
        label="Export Data"
        @click="exportToExcel"
      />
    </div>
    <div class="instrumentations-list">
      <div class="locations-tabs">
        <template v-if="sites && sites.length > 0">
          <b-tabs v-model="activeTab" :vertical="true" :animated="false">
            <b-tab-item v-for="site in sites" :key="site._id">
              <template #header>
                <b-tag
                  type="is-primary"
                  rounded
                  v-if="countsBySiteId[site._id] > 0"
                  style="margin-right: 5px"
                >
                  {{ countsBySiteId[site._id] }}
                </b-tag>
                <div class="tab-header">
                  {{ site.siteName }}
                </div>
              </template>
            </b-tab-item>
          </b-tabs>
        </template>
        <template v-else>
          <b-loading :is-full-page="false" :active="true" :can-cancel="false" />
        </template>
      </div>
      <div class="table-container">
        <b-table
          :data="instrumentations"
          :narrowed="true"
          :loading="isLoading"
          :paginated="instrumentations.length > 20 ? true : false"
          :per-page="20"
          @click="onRowClick"
        >
          <template slot-scope="props">
            <b-table-column field="instrumentId" label="Instrument ID" sortable>
              {{ props.row.instrumentId }}
            </b-table-column>

            <b-table-column field="instrumentName" label="Name" sortable>
              {{ props.row.instrumentName }}
            </b-table-column>

            <b-table-column field="typeName" label="Type Name" sortable>
              {{ props.row.typeName }}
            </b-table-column>

            <b-table-column
              field="calibrationDueDate"
              label="Calibration Due Date"
              centered
              sortable
            >
              {{ new Date(props.row.calibrationDueDate).toLocaleDateString() }}
            </b-table-column>

            <b-table-column
              field="lastCalibrationDate"
              label="Last Calibration Date"
              centered
              sortable
            >
              {{ new Date(props.row.lastCalibrationDate).toLocaleDateString() }}
            </b-table-column>

            <b-table-column field="instrumentStatus" label="Status" sortable>
              {{
                INSTRUMENTATION_STATUS.find(
                  (status) => status.value === props.row.status
                )?.title
              }}
            </b-table-column>
          </template>
          <template #empty>
            <div class="has-text-centered">No records</div>
          </template>
        </b-table>
      </div>
      <AddIntrumentModal
        v-if="isModalOpen"
        @close="closeAddInstrumentation"
        @onSave="onAddNew"
        @onUpdate="onUpdate"
        @downloadTemplate="downloadTemplateFile"
        @fileParsed="onFileParsedCheck"
        :instrumentation="selectedInstrumentation"
      />
      <InstrumentDetailsModal
        :selectedSite="sites[activeTab]"
        v-if="detailsIntrumentation"
        :propInstrumentation="detailsIntrumentation"
        @onUpdate="onUpdate"
        @close="detailsIntrumentation = null"
      />
    </div>
  </div>
</template>

<script>
import * as xlsx from 'xlsx';
import { v4 } from 'uuid';
import AddIntrumentModal from './AddIntrumentModal';
import InstrumentDetailsModal from './InstrumentDetailsModal';
import {
  FETCH_INSTRUMENTATIONS,
  ADD_INSTRUMENTATION,
  UPDATE_INSTRUMENTATION,
} from '../../../store/instrumentations/actions/actionTypes';
import {
  GET_INSTRUMENTATIONS,
  GET_INSTRUMENTATION_COUNTS_BY_SITE_ID,
} from '../../../store/instrumentations/getters/getterTypes';
import { INSTRUMENTATION_STATUS } from './constants';

const uuid = v4;
export default {
  name: 'Instrumentations',

  components: {
    AddIntrumentModal,
    InstrumentDetailsModal,
  },

  props: {
    search: {
      type: String,
      default: '',
    },
  },

  computed: {
    sites() {
      return this.$store.state.sites;
    },

    instrumentations() {
      return this.$store.getters[GET_INSTRUMENTATIONS];
    },

    mteTypes() {
      return this.$store.state.mteTypes;
    },

    countsBySiteId() {
      return this.$store.getters[GET_INSTRUMENTATION_COUNTS_BY_SITE_ID];
    },

    user() {
      return this.$store.state.currentUser;
    },
  },

  watch: {
    activeTab() {
      if (this.searchText && this.searchText.trim() !== '') {
        this.getInstrumentations({
          search: this.searchText,
        });
      } else {
        this.getInstrumentations();
      }
    },
    search(newValue) {
      this.searchText = newValue;
    },
  },

  data() {
    return {
      activeTab: 0,
      isLoading: false,
      isModalOpen: false,
      selectedInstrumentation: null,
      detailsIntrumentation: null,
      INSTRUMENTATION_STATUS,
      searchText: '',
    };
  },

  async mounted() {
    if (this.sites.length === 0) {
      await this.$store.dispatch('getSites');
    }
    const userPrimarySiteIndex = this.sites.findIndex(
      (site) => site._id === this.user.location
    );
    if (userPrimarySiteIndex !== -1) {
      this.activeTab = userPrimarySiteIndex;
    }

    this.getInstrumentations();
  },

  methods: {
    async getInstrumentations(query) {
      this.isLoading = true;
      await this.$store.dispatch(FETCH_INSTRUMENTATIONS, {
        siteId: this.sites[this.activeTab]._id,
        query,
      });
      this.isLoading = false;
    },
    openAddInstrumentation() {
      this.selectedInstrumentation = null;
      this.isModalOpen = true;
    },
    closeAddInstrumentation() {
      this.isModalOpen = false;
    },

    onRowClick(row) {
      this.detailsIntrumentation = row;
    },

    async onAddNew(newInstrument) {
      try {
        await this.$store.dispatch(ADD_INSTRUMENTATION, {
          siteId: this.sites[this.activeTab]._id,
          instrumentationData: {
            _id: uuid(),
            ...newInstrument,
          },
        });
        this.closeAddInstrumentation();
      } catch (error) {
        if (error.data && error.data.duplicate) {
          this.$buefy.toast.open({
            message: `Instrument ID: ${error.data.instrumentId} already exists.`,
            type: 'is-danger',
            duration: 5000,
          });
        }
      }
    },

    onFileParsedCheck(rows) {
      const existingIds = rows.filter((row) =>
        this.instrumentations.find(
          (instrument) =>
            instrument.instrumentId ===
            String(row['Instrument ID'] || '').trim()
        )
      );

      if (existingIds.length > 0) {
        const duplicateIds = existingIds.map((row) =>
          String(row['Instrument ID'] || '').trim()
        );

        this.$buefy.dialog.confirm({
          title: 'Existing Instrument ',
          message: `The following Instrument IDs already exist : ${duplicateIds.join(
            ', '
          )}`,
          confirmText: 'Override Instrument Data',
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            this.onFileParsed(
              rows,
              existingIds.map((row) =>
                String(row['Instrument ID'] || '').trim()
              )
            );
          },
        });
      } else {
        this.onFileParsed(rows);
      }
    },

    checkRequiredFields(instrument) {
      const validation =
        instrument.instrumentId &&
        instrument.instrumentId.length > 0 &&
        instrument.instrumentName &&
        instrument.instrumentName.length > 0 &&
        instrument.calibrationDueDate instanceof Date &&
        instrument.lastCalibrationDate instanceof Date;

      if (!validation) {
        this.$buefy.toast.open({
          message: `Please fill in all required fields for instrument: ${instrument.instrumentId}`,
          type: 'is-danger',
          duration: 5000,
        });
        return false;
      }

      if (instrument.calibrationDueDate < instrument.lastCalibrationDate) {
        this.$buefy.toast.open({
          message: `Calibration Due Date cannot be before Last Calibration Date for instrument: ${instrument.instrumentId}`,
          type: 'is-danger',
          duration: 5000,
        });
        return false;
      }

      return true;
    },

    onFileParsed(rows, existingIds = []) {
      const invalidRows = rows.filter(
        (row) =>
          !this.mteTypes.some(
            (type) =>
              type.name.toLowerCase() ===
              String(row['Type Name'] || '')
                .toLowerCase()
                .trim()
          )
      );

      const invalidTypeNames = Array.from(
        new Set(
          invalidRows
            .map((row) => String(row['Type Name'] || '').trim())
            .filter((name) => name.length)
        )
      );

      if (invalidRows.length > 0) {
        this.$buefy.toast.open({
          message: `Error: Some rows contain invalid Type Names: ${invalidTypeNames.join(
            ', '
          )}`,
          type: 'is-danger',
          duration: 5000,
          pauseOnHover: true,
        });
        return;
      }

      const rowsToAdd = [];
      const rowsToUpdate = [];
      rows.forEach((row) => {
        const typeId = this.mteTypes.find(
          (type) =>
            type.name.toLowerCase() ===
            String(row['Type Name'] || '')
              .toLowerCase()
              .trim()
        )?._id;

        const status = this.INSTRUMENTATION_STATUS.find(
          (s) =>
            s.title.toLocaleLowerCase() ===
            String(row.Status || '')
              .toLowerCase()
              .trim()
        );

        let calibrationSchedule = Number(row['Calibration Schedule (months)']);
        if (
          isNaN(calibrationSchedule) &&
          row['Calibration Schedule (months)'].toLowerCase() === 'manual'
        ) {
          calibrationSchedule = 'manual';
        }

        const lastCalibrationDate = this.parseExcelDateSafe(
          row['Last Calibration Date']
        );

        let calibrationDueDate = this.parseExcelDateSafe(
          row['Calibration Due Date']
        );

        if (calibrationSchedule !== 'manual') {
          calibrationDueDate = new Date(
            lastCalibrationDate.getFullYear(),
            lastCalibrationDate.getMonth() +
              Number(row['Calibration Schedule (months)']),
            lastCalibrationDate.getDate()
          );
        }

        const instrument = {
          _id: uuid(),
          instrumentId: row['Instrument ID'],
          instrumentName: row.Name,
          typeName: row['Type Name'],
          typeId,
          status: status ? status.value : '',
          calibrationSchedule,
          calibrationDueDate,
          lastCalibrationDate,
          storageLocation: row['Storage Location'],
          responsible: row.Responsible,
        };

        if (!this.checkRequiredFields(instrument)) return;

        if (!existingIds.includes(instrument.instrumentId))
          rowsToAdd.push(instrument);
        else {
          const id = this.instrumentations.find(
            (inst) => inst.instrumentId === instrument.instrumentId
          )?._id;
          rowsToUpdate.push({ ...instrument, _id: id });
        }
      });

      if (rowsToAdd.length > 0) {
        this.$store.dispatch(ADD_INSTRUMENTATION, {
          siteId: this.sites[this.activeTab]._id,
          instrumentationData: rowsToAdd,
        });
      }

      if (rowsToUpdate.length > 0) {
        this.$store.dispatch(UPDATE_INSTRUMENTATION, {
          siteId: this.sites[this.activeTab]._id,
          instrumentationData: rowsToUpdate,
        });
      }
      this.closeAddInstrumentation();
    },

    parseExcelDateSafe(value) {
      if (!value) return null;

      // If already a JS Date (what we WANT from cellDates: true)
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      // If Excel serial number
      if (typeof value === 'number') {
        // Using xlsx's date code parser is safest (handles Excel date system)
        const d = xlsx.SSF.parse_date_code(value);
        if (!d || !d.y || !d.m || !d.d) return null;
        return new Date(d.y, d.m - 1, d.d);
      }

      if (typeof value === 'string') {
        const s = value.trim();
        if (!s) return null;

        const dot = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (dot) {
          const dd = Number(dot[1]);
          const mm = Number(dot[2]);
          const yyyy = Number(dot[3]);
          const dt = new Date(yyyy, mm - 1, dd);
          return Number.isNaN(dt.getTime()) ? null : dt;
        }

        const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (slash) {
          const mm = Number(slash[1]);
          const dd = Number(slash[2]);
          const yyyy = Number(slash[3]);
          const dt = new Date(yyyy, mm - 1, dd);
          return Number.isNaN(dt.getTime()) ? null : dt;
        }

        const dt = new Date(s);
        return Number.isNaN(dt.getTime()) ? null : dt;
      }

      return null;
    },

    onUpdate(updatedInstrument) {
      this.$store.dispatch(UPDATE_INSTRUMENTATION, {
        instrumentationData: {
          ...updatedInstrument,
        },
        instrumentationId: updatedInstrument._id,
        siteId: this.sites[this.activeTab]._id,
      });
      this.closeAddInstrumentation();
    },

    excelSerialDateOnly(value) {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;

      const msPerDay = 24 * 60 * 60 * 1000;
      const utcDateOnly = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
      const excelEpoch = Date.UTC(1899, 11, 30);

      return Math.round((utcDateOnly - excelEpoch) / msPerDay);
    },

    exportToExcel() {
      const data = this.instrumentations.map((instrument) => ({
        'Instrument ID': instrument.instrumentId,
        Name: instrument.instrumentName,
        'Type Name': instrument.typeName.trim(),
        Status: this.INSTRUMENTATION_STATUS.find(
          (status) => status.value === instrument.status
        )?.title,

        'Calibration Due Date': instrument.calibrationDueDate
          ? new Date(instrument.calibrationDueDate)
          : null,

        'Last Calibration Date': instrument.lastCalibrationDate
          ? new Date(instrument.lastCalibrationDate)
          : null,

        'Calibration Schedule (months)': instrument.calibrationSchedule,
        'Storage Location': instrument.storageLocation,
        Responsible: instrument.responsible,
      }));

      const worksheet = xlsx.utils.json_to_sheet(data, { cellDates: true });

      const ref = worksheet['!ref'];
      if (ref) {
        const range = xlsx.utils.decode_range(ref);

        const headers = [];
        for (let C = range.s.c; C <= range.e.c; C++) {
          const addr = xlsx.utils.encode_cell({ r: 0, c: C });
          headers[C] = worksheet[addr]?.v;
        }

        const dueCol = headers.indexOf('Calibration Due Date');
        const lastCol = headers.indexOf('Last Calibration Date');

        for (let R = 1; R <= range.e.r; R++) {
          [dueCol, lastCol].forEach((colIdx) => {
            if (colIdx < 0) return;

            const cellAddr = xlsx.utils.encode_cell({ r: R, c: colIdx });
            const cell = worksheet[cellAddr];
            if (!cell || cell.v == null) return;

            const serial = this.excelSerialDateOnly(cell.v);
            if (serial == null) return;

            cell.t = 'n';
            cell.v = serial;
            cell.z = 'dd.mm.yyyy';
            delete cell.w;
          });
        }
      }

      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Instrumentation');

      xlsx.writeFile(
        workbook,
        `${this.sites[this.activeTab].siteName}- Instrumentation.xlsx`
      );
    },

    downloadTemplateFile() {
      const data = [
        {
          'Instrument ID': '',
          Name: '',
          'Type Name': '',
          Status: '',
          'Calibration Due Date': '',
          'Last Calibration Date': '',
          'Calibration Schedule (months)': '',
          'Storage Location': '',
          Responsible: '',
        },
      ];

      const worksheet = xlsx.utils.json_to_sheet(data);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Instrumentations');

      xlsx.writeFile(workbook, 'Template-Instrumentation.xlsx');
    },
  },
};
</script>
<style scoped>
.locations-tabs {
  max-width: 300px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}
.table-container {
  flex-grow: 1;
  overflow: hidden;
}

.instrumentations-list {
  display: flex;
  height: calc(100% - 56px);
  margin-top: 10px;
  margin-left: 10px;
  margin-right: 10px;
}

.export-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  padding: 10px;
  div {
    margin-bottom: 0;
  }
}

.tab-header {
  max-width: 280px;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.locations-tabs ::v-deep .tabs {
  height: 100%;
}

.locations-tabs ::v-deep .tabs ul {
  height: 100%;
}
td {
  cursor: pointer;
}
</style>
